import { useState } from "react";
import useSWR from "swr";
import { api, fmtEUR, fmtMonth } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Trophy, Warning, ChartBar } from "@phosphor-icons/react";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Reports() {
    const { data: report } = useSWR("/reports/summary", fetcher);
    const { data: missing } = useSWR("/reports/missing", fetcher);
    const { data: invoices } = useSWR("/invoices", fetcher);

    const monthlyData = (report?.monthly || []).map((m) => ({ ...m, label: fmtMonth(m.period) }));
    const avgMonth = monthlyData.length ? monthlyData.reduce((a, m) => a + m.total, 0) / monthlyData.length : 0;
    const topMonth = monthlyData.reduce((acc, m) => (m.total > (acc?.total || 0) ? m : acc), null);

    // Top items
    const itemAgg = {};
    (invoices || []).forEach((inv) => {
        (inv.items || []).forEach((it) => {
            const key = it.title;
            itemAgg[key] = (itemAgg[key] || 0) + (it.unit_price || 0) * (it.quantity || 1);
        });
    });
    const topItems = Object.entries(itemAgg).sort((a, b) => b[1] - a[1]).slice(0, 6);

    return (
        <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
            <div className="mb-8">
                <p className="overline">Rapports</p>
                <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
                    Analyse de vos dépenses
                </h1>
                <p className="text-muted-foreground text-sm mt-2 max-w-lg">
                    Vue d’ensemble des tendances mensuelles, des articles les plus coûteux
                    et des factures manquantes.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 ec-stagger">
                <StatBig label="Total cumulé" value={fmtEUR(report?.total_spent || 0)} sub={`${report?.total_invoices || 0} factures`} />
                <StatBig label="Moyenne / mois" value={fmtEUR(avgMonth)} sub={`sur ${monthlyData.length} mois`} />
                <StatBig
                    label="Mois le plus cher"
                    value={topMonth ? fmtEUR(topMonth.total) : "—"}
                    sub={topMonth ? topMonth.label : "—"}
                    accent={Trophy}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="ec-card lg:col-span-2">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="font-display text-lg font-bold tracking-tight">
                                Dépenses mensuelles
                            </h3>
                            <ChartBar weight="duotone" className="text-secondary" size={18} />
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">Montants TTC par mois (EUR)</p>
                        <div className="h-72">
                            {monthlyData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
                                    Pas encore de données
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}€`} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "hsl(var(--card))",
                                                border: "1px solid hsl(var(--border))",
                                                borderRadius: 6,
                                                fontSize: 12,
                                            }}
                                            formatter={(value) => [fmtEUR(value), "Total"]}
                                        />
                                        <Bar dataKey="total" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="ec-card border-amber-300 bg-amber-50/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Warning weight="fill" className="text-amber-600" size={20} />
                            <h3 className="font-display text-lg font-bold tracking-tight">Anomalies</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                            Factures sans PDF et ruptures de numérotation détectées
                        </p>
                        {(!missing || missing.length === 0) ? (
                            <div className="text-sm text-muted-foreground py-6 text-center">
                                Aucune anomalie détectée ✓
                            </div>
                        ) : (
                            <ul className="space-y-2 max-h-72 overflow-auto">
                                {missing.map((m, i) => (
                                    <li key={i} className="text-sm p-2 rounded-md bg-amber-100/50">
                                        {m.type === "missing_pdf" && (
                                            <>
                                                <div className="font-mono text-xs text-muted-foreground">{m.order_id}</div>
                                                <div className="text-xs">PDF de facture manquant — {fmtEUR(m.total_ttc)}</div>
                                            </>
                                        )}
                                        {m.type === "missing_number" && (
                                            <>
                                                <div className="text-xs font-semibold">Numéro manquant : {m.missing_sequence}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    Entre {m.after_invoice} et {m.before_invoice}
                                                </div>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <Card className="ec-card lg:col-span-3">
                    <CardContent className="p-6">
                        <h3 className="font-display text-lg font-bold tracking-tight mb-1">
                            Top articles par dépense
                        </h3>
                        <p className="text-xs text-muted-foreground mb-4">Les articles qui pèsent le plus dans votre budget</p>
                        {topItems.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                                Pas de données
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {topItems.map(([title, total], i) => {
                                    const max = topItems[0][1];
                                    const pct = (total / max) * 100;
                                    return (
                                        <li key={i}>
                                            <div className="flex items-center justify-between text-sm mb-1">
                                                <div className="truncate font-semibold">{title}</div>
                                                <div className="font-mono tabular text-sm">{fmtEUR(total)}</div>
                                            </div>
                                            <div className="h-1.5 bg-accent rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary rounded-full transition-all"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function StatBig({ label, value, sub, accent: AccentIcon }) {
    return (
        <Card className="ec-card ec-card-hover">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="overline">{label}</p>
                        <p className="font-display text-3xl font-bold mt-2 tabular tracking-tight">{value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{sub}</p>
                    </div>
                    {AccentIcon && <AccentIcon weight="duotone" size={22} className="text-primary" />}
                </div>
            </CardContent>
        </Card>
    );
}
